import React, { useState, useCallback } from 'react';
import { formatCurrency, formatCompact } from '../lib/utils';
import {
  yearlyTotals, tamData, TAM_TOTAL, objectifs, preLaunchBudget,
  valorisation, LICORNE_YEAR
} from '../mock';
import {
  TrendingUp, Shield, Users, School, ChefHat, Building2, Layers,
  Lock, Eye, Zap, Globe, Rocket, Target, DollarSign, Code,
  BarChart3, Network, Award, Database, ArrowRight, CheckCircle2,
  X, Check, Landmark, HeartHandshake, Baby, Plug, Star, Clock,
  FileCode2, BrainCircuit, ChevronDown, ArrowDown, ArrowLeftRight,
  CreditCard, MessageCircle, Bot, Repeat, Fingerprint, AlertTriangle,
  Wallet, Shuffle, BookOpen, FileDown, Languages
} from 'lucide-react';
import { investDeckT } from '../investDeckTranslations';

const LOGO = 'https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/5cp42cda_image.png';
const VIDEOS = {
  animated: 'https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/yzivaxf2_Logo%20Animee.mp4',
  presentation: 'https://customer-assets.emergentagent.com/job_8a79716d-d35e-4a82-acf8-054650228f44/artifacts/6x6axo0x_Pr%C3%A9sentation%20Ecolunch.mp4',
};

const total5 = yearlyTotals.reduce((acc, y) => ({ revenus: acc.revenus + y.revenus, baiia: acc.baiia + y.baiia }), { revenus: 0, baiia: 0 });
const licorneVal = valorisation.find(v => v.annee === LICORNE_YEAR) || {};

/* ── Sub-components ── */
function SectionTitle({ children, accent = 'lime' }) {
  const c = { lime: 'from-lime-400 to-cyan-400', cyan: 'from-cyan-400 to-lime-400', orange: 'from-orange-400 to-lime-400' };
  return (
    <div className="mb-8">
      <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-black bg-gradient-to-r ${c[accent]} bg-clip-text text-transparent`}>{children}</h2>
      <div className={`w-24 h-1 mt-2 rounded bg-gradient-to-r ${c[accent]}`} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'lime' }) {
  const c = {
    lime: { text: 'text-lime-400', border: 'border-lime-400/60', bg: 'bg-lime-500/20', shadow: 'shadow-[0_0_20px_rgba(132,204,22,0.3)] hover:shadow-[0_0_35px_rgba(132,204,22,0.5)]' },
    cyan: { text: 'text-cyan-400', border: 'border-cyan-400/60', bg: 'bg-cyan-500/20', shadow: 'shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_35px_rgba(6,182,212,0.5)]' },
    orange: { text: 'text-orange-400', border: 'border-orange-400/60', bg: 'bg-orange-500/20', shadow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)] hover:shadow-[0_0_35px_rgba(249,115,22,0.5)]' },
  };
  const s = c[color];
  return (
    <div className={`${s.bg} backdrop-blur-md bg-gray-900/80 border-2 ${s.border} rounded-xl p-5 text-center ${s.shadow} hover:scale-[1.03] transition-all duration-300`}>
      <Icon className={`h-8 w-8 mx-auto mb-2 ${s.text} drop-shadow-[0_0_8px_currentColor]`} />
      <p className={`text-2xl sm:text-3xl font-black ${s.text}`}>{value}</p>
      <p className="text-xs sm:text-sm text-gray-200 mt-1 font-medium">{label}</p>
    </div>
  );
}

function WhyCard({ icon: Icon, title, desc, color = 'lime' }) {
  const c = {
    lime: { border: 'border-lime-400/50', text: 'text-lime-400', shadow: 'hover:shadow-[0_0_30px_rgba(132,204,22,0.35)]', glow: 'hover:border-lime-400/80' },
    cyan: { border: 'border-cyan-400/50', text: 'text-cyan-400', shadow: 'hover:shadow-[0_0_30px_rgba(6,182,212,0.35)]', glow: 'hover:border-cyan-400/80' },
    orange: { border: 'border-orange-400/50', text: 'text-orange-400', shadow: 'hover:shadow-[0_0_30px_rgba(249,115,22,0.35)]', glow: 'hover:border-orange-400/80' },
    yellow: { border: 'border-yellow-400/50', text: 'text-yellow-400', shadow: 'hover:shadow-[0_0_30px_rgba(250,204,21,0.35)]', glow: 'hover:border-yellow-400/80' },
    pink: { border: 'border-pink-400/50', text: 'text-pink-400', shadow: 'hover:shadow-[0_0_30px_rgba(236,72,153,0.35)]', glow: 'hover:border-pink-400/80' },
    red: { border: 'border-red-400/50', text: 'text-red-400', shadow: 'hover:shadow-[0_0_30px_rgba(248,113,113,0.35)]', glow: 'hover:border-red-400/80' },
    purple: { border: 'border-purple-400/50', text: 'text-purple-400', shadow: 'hover:shadow-[0_0_30px_rgba(168,85,247,0.35)]', glow: 'hover:border-purple-400/80' },
  };
  const s = c[color];
  return (
    <div className={`bg-gray-900/90 backdrop-blur-md border-2 ${s.border} ${s.glow} rounded-xl p-6 ${s.shadow} hover:scale-[1.02] transition-all duration-300`}>
      <Icon className={`h-8 w-8 ${s.text} mb-3 drop-shadow-[0_0_8px_currentColor]`} />
      <h4 className="font-bold text-white mb-2 text-base">{title}</h4>
      <p className="text-sm text-gray-200 leading-relaxed">{desc}</p>
    </div>
  );
}

function FlowStep({ num, title, desc }) {
  return (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 rounded-full bg-lime-500/20 border-2 border-lime-500/50 flex items-center justify-center flex-shrink-0 group-hover:shadow-[0_0_15px_rgba(132,204,22,0.4)] transition-all duration-300">
        <span className="text-lime-400 font-bold text-sm">{num}</span>
      </div>
      <div>
        <p className="font-semibold text-white">{title}</p>
        <p className="text-sm text-gray-300">{desc}</p>
      </div>
    </div>
  );
}

function PhaseCard({ num, title, period, desc, color }) {
  const c = {
    lime: { border: 'border-lime-400/60', text: 'text-lime-400', shadow: 'hover:shadow-[0_0_25px_rgba(132,204,22,0.3)]' },
    cyan: { border: 'border-cyan-400/60', text: 'text-cyan-400', shadow: 'hover:shadow-[0_0_25px_rgba(6,182,212,0.3)]' },
    orange: { border: 'border-orange-400/60', text: 'text-orange-400', shadow: 'hover:shadow-[0_0_25px_rgba(249,115,22,0.3)]' },
    purple: { border: 'border-purple-400/60', text: 'text-purple-400', shadow: 'hover:shadow-[0_0_25px_rgba(168,85,247,0.3)]' },
  };
  const s = c[color];
  return (
    <div className={`bg-gray-900/90 backdrop-blur-md border-2 ${s.border} rounded-xl p-5 relative ${s.shadow} hover:scale-[1.02] transition-all duration-300`}>
      <div className={`absolute -top-3 left-4 px-3 py-0.5 rounded-full bg-gray-900 border-2 ${s.border} text-xs font-bold ${s.text}`}>
        Phase {num}
      </div>
      <h4 className="font-bold text-white mt-2 mb-1">{title}</h4>
      <p className="text-xs text-gray-400 mb-2">{period}</p>
      <p className="text-sm text-gray-200">{desc}</p>
    </div>
  );
}

function ArchNode({ icon: Icon, title, subtitle, bg, border: borderColor, textColor, wide }) {
  return (
    <div className={`${bg} bg-gray-900/80 backdrop-blur-md border-2 ${borderColor} rounded-xl px-6 py-4 text-center ${wide ? 'w-full max-w-sm' : 'w-48'} mx-auto hover:scale-[1.04] transition-all duration-300`}>
      <Icon className={`h-7 w-7 mx-auto mb-1 ${textColor} drop-shadow-[0_0_8px_currentColor]`} />
      <p className="font-bold text-white text-sm">{title}</p>
      <p className="text-xs text-gray-300">{subtitle}</p>
    </div>
  );
}

const SSOT_ICONS = [Fingerprint, Shuffle, HeartHandshake, AlertTriangle, BarChart3, Landmark, Database, Star, MessageCircle, Bot];
const SSOT_COLORS = ['lime', 'cyan', 'orange', 'lime', 'cyan', 'orange', 'lime', 'cyan', 'orange', 'lime'];
const WHY_ICONS = [Globe, Award, DollarSign, Plug, HeartHandshake, Landmark, Star];
const WHY_COLORS = ['lime', 'cyan', 'orange', 'yellow', 'pink', 'red', 'purple'];
const OPPO_ICONS = [Shuffle, Repeat, Layers, Eye, Building2, Wallet, AlertTriangle, HeartHandshake, Database, Baby];
const PHASE_COLORS = ['lime', 'cyan', 'orange', 'purple'];

export default function InvestDeckPage() {
  const [lang, setLang] = useState('fr');
  const t = investDeckT[lang];
  const handleDownload = useCallback(() => { window.print(); }, []);
  const stakeColors = ['text-lime-400 border-lime-500/50', 'text-orange-400 border-orange-500/50', 'text-cyan-400 border-cyan-500/50', 'text-purple-400 border-purple-500/50', 'text-yellow-400 border-yellow-500/50'];

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">
      <style>{`
        @media print {
          .invest-deck-download-bar { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Floating toolbar */}
      <div className="invest-deck-download-bar" style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', gap: 8 }}>
        <button
          onClick={() => setLang(l => l === 'fr' ? 'en' : 'fr')}
          data-testid="invest-deck-lang-toggle"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white bg-gray-800/90 backdrop-blur-md border-2 border-gray-600 hover:border-cyan-400 transition-all duration-300 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)]"
        >
          <Languages size={18} />
          {lang === 'fr' ? 'EN' : 'FR'}
        </button>
        <button
          onClick={handleDownload}
          data-testid="invest-deck-download-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-black transition-all duration-300 shadow-[0_0_20px_rgba(132,204,22,0.4)] hover:shadow-[0_0_35px_rgba(132,204,22,0.6)] hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #84cc16, #06b6d4)' }}
        >
          <FileDown size={18} />
          {t.download}
        </button>
      </div>

      {/* Background fox pattern */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/ye5d9g73_image.png')`, backgroundSize: '180px', backgroundRepeat: 'repeat', backgroundPosition: 'center', opacity: 0.12 }} />
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_foodtech-finplan/artifacts/ye5d9g73_image.png')`, backgroundSize: '180px', backgroundRepeat: 'repeat', backgroundPosition: 'center', opacity: 0.08, filter: 'drop-shadow(0 0 8px rgba(168, 213, 48, 0.6)) drop-shadow(0 0 12px rgba(168, 213, 48, 0.4))' }} />

      {/* ═══════ HERO ═══════ */}
      <section className="relative z-10 min-h-screen flex items-center justify-center overflow-hidden bg-black/50">
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <video autoPlay loop muted playsInline poster={LOGO} className="h-24 sm:h-28 lg:h-32 w-auto mx-auto mb-4 sm:mb-6 drop-shadow-[0_0_30px_rgba(168,213,48,0.6)] hover:drop-shadow-[0_0_50px_rgba(168,213,48,0.9)] transition-all duration-300">
            <source src={VIDEOS.animated} type="video/mp4" />
            <img src={LOGO} alt="EcoLunch" className="h-24 sm:h-28 lg:h-32 w-auto" />
          </video>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-4">
            <span className="bg-gradient-to-r from-lime-400 via-cyan-400 to-lime-400 bg-clip-text text-transparent">EcoLunch</span>
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-3 font-light">{t.heroSub}</p>
          <p className="text-sm sm:text-base text-gray-500 mb-10 max-w-2xl mx-auto">{t.heroDesc}</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-3xl mx-auto mb-10">
            {t.stakeholders.map((p, i) => (
              <div key={i} className={`bg-gray-900/90 backdrop-blur-md rounded-xl p-3 border-2 ${stakeColors[i].split(' ')[1]} ${i === 4 ? 'col-span-2 sm:col-span-1' : ''} hover:scale-[1.05] transition-all duration-300`} style={{boxShadow: '0 0 15px rgba(0,0,0,0.3)'}}>
                <p className={`${stakeColors[i].split(' ')[0]} font-bold text-lg`}>{p.name}</p>
                <p className="text-xs text-gray-300">{p.sub}</p>
              </div>
            ))}
          </div>
          <a href="#resume" className="inline-flex items-center gap-2 text-sm text-gray-400 animate-bounce">
            <ChevronDown className="h-5 w-5" /> {t.discoverDeck}
          </a>
        </div>
      </section>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ═══════ 1. RÉSUMÉ EXÉCUTIF ═══════ */}
        <section id="resume" className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.resumeTitle}</SectionTitle>
          <p className="text-gray-300 leading-relaxed mb-8 max-w-3xl">{t.resumeDesc}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard icon={School} label={t.statSchools} value={objectifs.total.toLocaleString()} color="lime" />
            <StatCard icon={TrendingUp} label={t.statRevenues} value={formatCompact(total5.revenus)} color="cyan" />
            <StatCard icon={BarChart3} label={t.statBaiia} value={formatCompact(total5.baiia)} color="orange" />
            <StatCard icon={Globe} label={t.statProvinces} value="10" color="lime" />
          </div>
        </section>

        {/* ═══════ 2. L'OPPORTUNITÉ ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="cyan">{t.oppoTitle}</SectionTitle>
          <p className="text-gray-300 leading-relaxed mb-8 max-w-3xl">{t.oppoDesc}</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {t.oppoItems.map((label, i) => {
              const Icon = OPPO_ICONS[i];
              return (
                <div key={i} className="bg-gray-900/85 backdrop-blur-md border-2 border-red-500/50 rounded-xl p-4 text-center hover:shadow-[0_0_25px_rgba(239,68,68,0.4)] hover:border-red-400/80 hover:scale-[1.03] transition-all duration-300">
                  <Icon className="h-7 w-7 mx-auto mb-2 text-red-400 drop-shadow-[0_0_8px_rgba(239,68,68,0.6)]" />
                  <p className="text-xs sm:text-sm text-gray-200 font-medium leading-snug">{label}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-8 bg-gray-900/85 backdrop-blur-md border-2 border-lime-400/50 rounded-xl p-6 shadow-[0_0_20px_rgba(132,204,22,0.2)]">
            <p className="text-lime-400 font-bold mb-2 text-lg">TAM : {TAM_TOTAL.toLocaleString()} {t.tamLabel}</p>
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-3">
              {Object.entries(tamData).map(([prov, d]) => (
                <div key={prov} className="text-center">
                  <p className="text-xs font-bold text-white uppercase">{prov}</p>
                  <p className="text-xs text-gray-400">{d.total}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ 3. COMPRENDRE ECOLUNCH ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.understandTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-red-500/50 rounded-xl p-6 shadow-[0_0_15px_rgba(239,68,68,0.15)] hover:shadow-[0_0_25px_rgba(239,68,68,0.3)] transition-all duration-300">
              <div className="flex items-center gap-2 mb-4">
                <X className="h-6 w-6 text-red-400 drop-shadow-[0_0_6px_rgba(239,68,68,0.6)]" />
                <h3 className="text-lg font-bold text-red-400">{t.notTitle}</h3>
              </div>
              <ul className="space-y-2">
                {t.notItems.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <X className="h-4 w-4 text-red-400 flex-shrink-0" />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-lime-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.15)] hover:shadow-[0_0_25px_rgba(132,204,22,0.3)] transition-all duration-300">
              <div className="flex items-center gap-2 mb-4">
                <Check className="h-6 w-6 text-lime-400 drop-shadow-[0_0_6px_rgba(132,204,22,0.6)]" />
                <h3 className="text-lg font-bold text-lime-400">{t.areTitle}</h3>
              </div>
              <ul className="space-y-2">
                {t.areItems.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-white">
                    <Check className="h-4 w-4 text-lime-400 flex-shrink-0" /><span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ═══════ 4. AVANT vs APRÈS ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.beforeAfterTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-red-400 mb-4">{t.beforeTitle}</h3>
              {t.beforeItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-900/85 backdrop-blur-md border-2 border-red-500/40 rounded-lg px-4 py-3 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)] hover:border-red-400/70 transition-all duration-300">
                  <X className="h-5 w-5 text-red-400 flex-shrink-0 drop-shadow-[0_0_6px_rgba(239,68,68,0.5)]" />
                  <p className="text-sm text-gray-300">{item}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <h3 className="text-lg font-bold text-lime-400 mb-4">{t.afterTitle}</h3>
              {t.afterItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-900/85 backdrop-blur-md border-2 border-lime-500/40 rounded-lg px-4 py-3 hover:shadow-[0_0_20px_rgba(132,204,22,0.3)] hover:border-lime-400/70 transition-all duration-300">
                  <Check className="h-5 w-5 text-lime-400 flex-shrink-0 drop-shadow-[0_0_6px_rgba(132,204,22,0.5)]" />
                  <p className="text-sm text-white font-medium">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ 5. ARCHITECTURE ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="cyan">{t.archTitle}</SectionTitle>
          <p className="text-gray-300 mb-8">{t.archDesc}</p>
          <div className="bg-gray-900/90 backdrop-blur-md border-2 border-gray-600/50 rounded-2xl p-6 sm:p-10 shadow-[0_0_20px_rgba(6,182,212,0.1)]">
            <div className="flex flex-col items-center gap-2">
              <ArchNode icon={Landmark} title={t.archNodes.css} subtitle={t.archNodes.cssS} bg="bg-red-500/20" border="border-red-500" textColor="text-red-400" wide />
              <div className="w-0.5 h-6 bg-gradient-to-b from-red-500 to-emerald-500" />
              <ArchNode icon={Eye} title={t.archNodes.dir} subtitle={t.archNodes.dirS} bg="bg-emerald-500/20" border="border-emerald-500" textColor="text-emerald-400" wide />
              <div className="w-0.5 h-6 bg-gradient-to-b from-emerald-500 to-purple-500" />
              <div className="flex items-center gap-4 sm:gap-8">
                <ArchNode icon={School} title={t.archNodes.schools} subtitle={t.archNodes.schoolsS} bg="bg-purple-500/20" border="border-purple-500" textColor="text-purple-400" />
                <ArrowLeftRight className="h-5 w-5 text-cyan-400" />
                <ArchNode icon={ChefHat} title={t.archNodes.caterers} subtitle={t.archNodes.caterersS} bg="bg-orange-500/20" border="border-orange-500" textColor="text-orange-400" />
              </div>
              <div className="w-0.5 h-6 bg-gradient-to-b from-purple-500 to-pink-500" />
              <ArchNode icon={Users} title={t.archNodes.parents} subtitle={t.archNodes.parentsS} bg="bg-pink-500/20" border="border-pink-500" textColor="text-pink-400" wide />
              <div className="w-0.5 h-6 bg-gradient-to-b from-pink-500 to-cyan-500" />
              <ArchNode icon={HeartHandshake} title={t.archNodes.coparen} subtitle={t.archNodes.coparenS} bg="bg-cyan-500/20" border="border-cyan-500" textColor="text-cyan-400" wide />
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {t.archBadges.map((badge, i) => (
                <span key={i} className="px-4 py-1.5 rounded-full bg-lime-500/20 border border-lime-500/50 text-sm font-bold text-lime-400">{badge}</span>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ 6. SINGLE SOURCE OF TRUTH ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.ssotTitle}</SectionTitle>
          <div className="bg-gray-900/90 backdrop-blur-md border-2 border-lime-400/50 rounded-2xl p-8 shadow-[0_0_30px_rgba(132,204,22,0.15)]">
            <Database className="h-12 w-12 mx-auto mb-4 text-lime-400 drop-shadow-[0_0_12px_rgba(132,204,22,0.6)]" />
            <p className="text-xl sm:text-2xl font-black text-white mb-4 text-center">{t.ssotHeadline}</p>
            <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto mb-8 text-center">{t.ssotDesc}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {t.ssotCards.map((item, i) => {
                const Icon = SSOT_ICONS[i];
                const color = SSOT_COLORS[i];
                const colors = {
                  lime: { border: 'border-lime-400/40', text: 'text-lime-400', bg: 'bg-lime-500/10', shadow: 'hover:shadow-[0_0_25px_rgba(132,204,22,0.3)]', glow: 'hover:border-lime-400/70' },
                  cyan: { border: 'border-cyan-400/40', text: 'text-cyan-400', bg: 'bg-cyan-500/10', shadow: 'hover:shadow-[0_0_25px_rgba(6,182,212,0.3)]', glow: 'hover:border-cyan-400/70' },
                  orange: { border: 'border-orange-400/40', text: 'text-orange-400', bg: 'bg-orange-500/10', shadow: 'hover:shadow-[0_0_25px_rgba(249,115,22,0.3)]', glow: 'hover:border-orange-400/70' },
                };
                const s = colors[color];
                return (
                  <div key={i} className={`${s.bg} bg-gray-950/60 backdrop-blur-sm border-2 ${s.border} ${s.glow} rounded-xl p-5 ${s.shadow} hover:scale-[1.02] transition-all duration-300`}>
                    <div className="flex items-start gap-4">
                      <Icon className={`h-8 w-8 ${s.text} flex-shrink-0 mt-0.5 drop-shadow-[0_0_8px_currentColor]`} />
                      <div>
                        <h4 className="font-bold text-white mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-200 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════ 7. PRODUIT & EXPÉRIENCE ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.productTitle}</SectionTitle>
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <p className="text-gray-300 leading-relaxed mb-6">{t.productDesc}</p>
              <div className="space-y-5">
                {t.flowSteps.map((step, i) => (
                  <FlowStep key={i} num={String(i + 1)} title={step.title} desc={step.desc} />
                ))}
              </div>
            </div>
            <div className="rounded-xl overflow-hidden border border-gray-700/50 shadow-2xl shadow-lime-500/5">
              <video controls playsInline poster={LOGO} className="w-full" data-testid="presentation-video">
                <source src={VIDEOS.presentation} type="video/mp4" />
              </video>
              <div className="bg-gray-900/80 p-3 text-center">
                <p className="text-xs text-gray-400">{t.videoCaption}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════ 8. POURQUOI ECOLUNCH ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="cyan">{t.whyTitle}</SectionTitle>
          <p className="text-gray-300 mb-8">{t.whyDesc}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.whyCards.map((card, i) => (
              <WhyCard key={i} icon={WHY_ICONS[i]} title={card.title} desc={card.desc} color={WHY_COLORS[i]} />
            ))}
          </div>
        </section>

        {/* ═══════ 9. SÉCURITÉ ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.secTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-bold text-white mb-4">{t.secCertsTitle}</h3>
              <div className="space-y-3">
                {t.secCerts.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-900/85 backdrop-blur-md border-2 border-orange-500/40 rounded-lg p-3 hover:shadow-[0_0_20px_rgba(249,115,22,0.25)] hover:border-orange-400/70 transition-all duration-300">
                    <Shield className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0 drop-shadow-[0_0_6px_rgba(249,115,22,0.5)]" />
                    <div>
                      <p className="font-bold text-white text-sm">{c.label}</p>
                      <p className="text-xs text-gray-300">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-4">{t.secArchTitle}</h3>
              <div className="space-y-3">
                {t.secArch.map((a, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-900/85 backdrop-blur-md border-2 border-cyan-500/40 rounded-lg p-3 hover:shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:border-cyan-400/70 transition-all duration-300">
                    <Lock className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0 drop-shadow-[0_0_6px_rgba(6,182,212,0.5)]" />
                    <div>
                      <p className="font-bold text-white text-sm">{a.label}</p>
                      <p className="text-xs text-gray-300">{a.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════ 10. MODÈLE D'AFFAIRES ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.modelTitle}</SectionTitle>
          <p className="text-gray-300 mb-8">{t.modelDesc}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-lime-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.15)] hover:shadow-[0_0_30px_rgba(132,204,22,0.35)] hover:scale-[1.02] transition-all duration-300">
              <DollarSign className="h-8 w-8 text-lime-400 mb-3 drop-shadow-[0_0_8px_rgba(132,204,22,0.6)]" />
              <h4 className="font-bold text-white mb-2">{t.saasTitle}</h4>
              <p className="text-sm text-gray-300 mb-3">{t.saasDesc}</p>
              <ul className="space-y-1 text-xs text-gray-400">
                {t.saasItems.map((s, i) => <li key={i}>- {s}</li>)}
              </ul>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-cyan-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(6,182,212,0.15)] hover:shadow-[0_0_30px_rgba(6,182,212,0.35)] hover:scale-[1.02] transition-all duration-300">
              <TrendingUp className="h-8 w-8 text-cyan-400 mb-3 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
              <h4 className="font-bold text-white mb-2">{t.ftTitle}</h4>
              <p className="text-sm text-cyan-300 font-semibold mb-3">{t.ftExclusive}</p>
              <ul className="space-y-1.5 text-xs text-gray-300">
                {t.ftItems.map((f, i) => <li key={i}>{f.bold && <span className="text-white font-bold">{f.bold}</span>}{f.text}</li>)}
              </ul>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-orange-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(249,115,22,0.15)] hover:shadow-[0_0_30px_rgba(249,115,22,0.35)] hover:scale-[1.02] transition-all duration-300">
              <CreditCard className="h-8 w-8 text-orange-400 mb-3 drop-shadow-[0_0_8px_rgba(249,115,22,0.6)]" />
              <h4 className="font-bold text-white mb-2">{t.morTitle}</h4>
              <p className="text-sm text-orange-300 font-semibold mb-3">{t.morSub}</p>
              <p className="text-xs text-gray-300 leading-relaxed">{t.morDesc}</p>
              <ul className="space-y-1 text-xs text-gray-300 mt-2">
                {t.morItems.map((m, i) => <li key={i}>- {m}</li>)}
              </ul>
              <p className="text-xs text-orange-400 font-bold mt-2">{t.morBottom}</p>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-purple-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(168,85,247,0.15)] hover:shadow-[0_0_30px_rgba(168,85,247,0.35)] hover:scale-[1.02] transition-all duration-300">
              <Star className="h-8 w-8 text-purple-400 mb-3 drop-shadow-[0_0_8px_rgba(168,85,247,0.6)]" />
              <h4 className="font-bold text-white mb-2">{t.premiumTitle}</h4>
              <p className="text-sm text-gray-300 mb-3">{t.premiumDesc}</p>
              <ul className="space-y-1 text-xs text-gray-400">
                {t.premiumItems.map((p, i) => <li key={i}>- {p}</li>)}
              </ul>
            </div>
          </div>
          <p className="text-center mt-6 text-sm text-gray-300 italic">{t.modelFooter}</p>
        </section>

        {/* ═══════ 11. EFFETS DE RÉSEAU ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.networkTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-lime-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.15)] hover:shadow-[0_0_25px_rgba(132,204,22,0.3)] transition-all duration-300">
              <Network className="h-8 w-8 text-lime-400 mb-3 drop-shadow-[0_0_8px_rgba(132,204,22,0.6)]" />
              <h4 className="font-bold text-white mb-3">{t.viralTitle}</h4>
              <p className="text-sm text-gray-300 mb-3">{t.viralDesc}</p>
              <ul className="space-y-2 text-sm text-gray-200">
                {t.viralItems.map((v, i) => (
                  <li key={i} className="flex items-center gap-2"><ArrowRight className="h-4 w-4 text-lime-400" />{v}</li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-cyan-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(6,182,212,0.15)] hover:shadow-[0_0_25px_rgba(6,182,212,0.3)] transition-all duration-300">
              <Users className="h-8 w-8 text-cyan-400 mb-3 drop-shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
              <h4 className="font-bold text-white mb-3">{t.parentTitle}</h4>
              <p className="text-sm text-gray-300 mb-3">{t.parentDesc}</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {t.parentItems.map((item, i) => (
                  <div key={i} className="bg-cyan-500/15 backdrop-blur-sm border-2 border-cyan-500/40 rounded-lg px-3 py-2 text-center hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300">
                    <p className="text-xs text-cyan-200 font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════ 12. GO-TO-MARKET ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="cyan">{t.gtmTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Plug className="h-5 w-5 text-cyan-400" /> {t.connectTitle}</h3>
              <p className="text-sm text-gray-300 mb-4">{t.connectDesc}</p>
              <div className="grid grid-cols-2 gap-2">
                {t.connectItems.map((s, i) => (
                  <div key={i} className="bg-cyan-500/15 backdrop-blur-sm border-2 border-cyan-500/40 rounded-lg p-3 text-center hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300">
                    <p className="text-xs text-cyan-200 font-medium">{s}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Award className="h-5 w-5 text-orange-400" /> {t.certTitle}</h3>
              <p className="text-sm text-gray-300 mb-4">{t.certDesc}</p>
              <div className="grid grid-cols-2 gap-2">
                {t.certItems.map((s, i) => (
                  <div key={i} className="bg-orange-500/15 backdrop-blur-sm border-2 border-orange-500/40 rounded-lg p-3 text-center hover:shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all duration-300">
                    <p className="text-xs text-orange-200 font-medium">{s}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════ 13. JALONS ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.milestonesTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.phases.map((p, i) => (
              <PhaseCard key={i} num={String(i + 1)} title={p.title} period={p.period} desc={p.desc} color={PHASE_COLORS[i]} />
            ))}
          </div>
        </section>

        {/* ═══════ 14. PLAN D'EXÉCUTION ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.execTitle}</SectionTitle>
          <div className="bg-gray-900/90 backdrop-blur-md border-2 border-gray-600/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.1)]">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-lime-500/20 border-2 border-lime-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(132,204,22,0.3)]">
                <Rocket className="h-6 w-6 text-lime-400 drop-shadow-[0_0_6px_rgba(132,204,22,0.6)]" />
              </div>
              <div>
                <p className="font-bold text-white">{t.execPrelaunch}</p>
                <p className="text-sm text-gray-400">{t.execPrelaunchSub}</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-gray-900/85 backdrop-blur-md border-2 border-lime-400/50 rounded-lg p-4 text-center hover:shadow-[0_0_20px_rgba(132,204,22,0.3)] transition-all duration-300">
                <p className="text-2xl font-black text-lime-400">{formatCurrency(preLaunchBudget.opex6mois)}</p>
                <p className="text-xs text-gray-200 mt-1">{t.execOpex}</p>
              </div>
              <div className="bg-gray-900/85 backdrop-blur-md border-2 border-cyan-400/50 rounded-lg p-4 text-center hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-300">
                <p className="text-2xl font-black text-cyan-400">{formatCurrency(preLaunchBudget.incitatifs)}</p>
                <p className="text-xs text-gray-200 mt-1">{t.execIncentives}</p>
              </div>
              <div className="bg-gray-900/85 backdrop-blur-md border-2 border-orange-400/50 rounded-lg p-4 text-center hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] transition-all duration-300">
                <p className="text-2xl font-black text-orange-400">{formatCurrency(preLaunchBudget.reserve)}</p>
                <p className="text-xs text-gray-200 mt-1">{t.execReserve}</p>
              </div>
            </div>
            <p className="text-center mt-4 text-sm text-gray-400">{t.execLaunch} <span className="text-white font-bold">Septembre 2026</span> — {t.execLaunchDetail}</p>
          </div>
        </section>

        {/* ═══════ 15. PI ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="cyan">{t.piTitle}</SectionTitle>
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <StatCard icon={Code} label={t.piCore} value="8 850h" color="cyan" />
            <StatCard icon={FileCode2} label={t.piTotal} value="9 850h" color="lime" />
            <StatCard icon={DollarSign} label={t.piCost} value="1.3M $" color="orange" />
          </div>
          <p className="text-sm text-gray-300 leading-relaxed">
            {t.piDesc} <span className="text-lime-400 font-bold">{t.piCliq}</span> {t.piCliqDetail}
          </p>
        </section>

        {/* ═══════ 16. DONNÉES PROPRIÉTAIRES ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="orange">{t.dataTitle}</SectionTitle>
          <div className="bg-gray-900/90 backdrop-blur-md border-2 border-orange-400/50 rounded-xl p-8 shadow-[0_0_20px_rgba(249,115,22,0.15)]">
            <Database className="h-10 w-10 text-orange-400 mb-4 drop-shadow-[0_0_10px_rgba(249,115,22,0.6)]" />
            <p className="text-lg text-gray-200 leading-relaxed mb-4">
              {t.dataIntro}<span className="text-lime-400 font-bold">{t.dataSystem}</span>{t.dataIntro2}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
              {t.dataItems.map((d, i) => (
                <div key={i} className="bg-orange-500/15 backdrop-blur-sm border-2 border-orange-500/40 rounded-lg p-3 text-center hover:shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all duration-300">
                  <p className="text-xs text-orange-200 font-medium">{d}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Ces données créent un <span className="text-orange-400 font-bold">{t.dataOutro1}</span> et des <span className="text-orange-400 font-bold">{t.dataOutro2}</span>{t.dataOutroFull}
            </p>
          </div>
        </section>

        {/* ═══════ 17. LEVÉE DE FONDS ═══════ */}
        <section className="py-16 sm:py-20 border-b border-gray-800">
          <SectionTitle accent="lime">{t.fundraiseTitle}</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-lime-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.15)]">
              <h3 className="text-lg font-bold text-lime-400 mb-3">{t.selfFund}</h3>
              <p className="text-sm text-gray-300 mb-4">{t.selfFundDesc} {formatCurrency(preLaunchBudget.total)}{t.selfFundDesc2}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t.opex6m}</span><span className="text-lime-400 font-bold">{formatCurrency(preLaunchBudget.opex6mois)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t.incentives}</span><span className="text-cyan-400 font-bold">{formatCurrency(preLaunchBudget.incitatifs)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-400">{t.reserve}</span><span className="text-orange-400 font-bold">{formatCurrency(preLaunchBudget.reserve)}</span></div>
                <div className="border-t border-gray-700 pt-2 flex justify-between text-sm font-bold"><span className="text-white">{t.total}</span><span className="text-white">{formatCurrency(preLaunchBudget.total)}</span></div>
              </div>
            </div>
            <div className="bg-gray-900/90 backdrop-blur-md border-2 border-cyan-400/50 rounded-xl p-6 shadow-[0_0_15px_rgba(6,182,212,0.15)]">
              <h3 className="text-lg font-bold text-cyan-400 mb-3">{t.opporFund}</h3>
              <p className="text-sm text-gray-300 mb-4">{t.opporFundDesc}</p>
              <p className="text-sm text-gray-300">{t.opporFundDesc2}</p>
            </div>
          </div>
          <div className="bg-gray-900/90 backdrop-blur-md border-2 border-gray-600/50 rounded-xl p-6 shadow-[0_0_15px_rgba(132,204,22,0.1)]">
            <h3 className="text-lg font-bold text-white mb-4 text-center">{t.valorTitle}</h3>
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
              {valorisation.map((v) => {
                const isLicorne = v.annee === LICORNE_YEAR;
                return (
                  <div key={v.annee} className={`rounded-lg p-3 text-center ${isLicorne ? 'bg-lime-500/25 border-2 border-lime-500/60' : 'bg-gray-900/70 border border-gray-600/40'}`}>
                    <p className={`text-xs font-bold ${isLicorne ? 'text-lime-400' : 'text-gray-400'}`}>{v.annee}</p>
                    <p className={`text-sm sm:text-base font-black ${isLicorne ? 'text-lime-300' : 'text-white'}`}>{formatCompact(v.valeur)}</p>
                    <p className="text-xs text-gray-500">{v.multiple}x</p>
                    {isLicorne && <p className="text-xs text-lime-400 font-bold mt-1">{t.unicorn}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════ FOOTER ═══════ */}
        <section className="py-20 text-center">
          <p className="text-xl sm:text-2xl font-black text-gray-300 italic mb-10 max-w-2xl mx-auto">
            {t.footerQuote}<br />{t.footerQuote2}
          </p>
          <div className="mb-8">
            <img src={LOGO} alt="EcoLunch" className="h-16 mx-auto mb-4 opacity-80" />
            <p className="text-2xl sm:text-3xl font-black text-white mb-2">EcoLunch</p>
            <p className="text-gray-400">{t.footerSub}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
            <div className="text-center">
              <p className="text-xl font-black text-lime-400">{objectifs.total.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{t.footerSchools}</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-cyan-400">{formatCompact(total5.revenus)}</p>
              <p className="text-xs text-gray-400">{t.footerRevenues}</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-orange-400">{formatCompact(licorneVal.valeur || 0)}</p>
              <p className="text-xs text-gray-400">{t.footerValo} {LICORNE_YEAR}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            {t.footerStack} &nbsp;|&nbsp; {t.footerHost} &nbsp;|&nbsp; {t.footerPay}
          </p>
        </section>
      </div>
    </div>
  );
}
